import type { CSSProperties, ReactNode } from "react";
import {
  maxGain,
  maxLoss,
  perpMargin,
  perpPnl,
  predPnl,
  type PayoffPoint,
  type PerpLeg,
  type PredictionLeg,
} from "../lib/composePayoff";

const PERP_COLOR = "#a78bfa";
const PRED_COLOR = "#60a5fa";
const BRAND = "#9ecbff";

// Use-case palette
const HEDGE_COLOR = "#60a5fa"; // blue
const AMPLIFY_COLOR = "#a78bfa"; // violet
const HARVEST_COLOR = "#f5a623"; // gold

function abs0Frac(abs: number): number {
  return abs < 10 ? 2 : 0;
}

function signedUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: abs0Frac(Math.abs(n)),
  });
  return neg ? `−${abs}` : `+${abs}`;
}

function formatPrice(n: number): string {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n >= 100 ? 0 : 2,
  });
}

// --------------------------------------------------------------------------
// Trigger — small white circle "?" that toggles the aside panel.
// --------------------------------------------------------------------------

const triggerBtnBase: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 11,
  background: "transparent",
  cursor: "pointer",
  fontFamily: "var(--f-mono)",
  fontSize: 12,
  fontWeight: 500,
  display: "grid",
  placeItems: "center",
  padding: 0,
  lineHeight: 1,
  color: "var(--fg-max)",
  border: "1px solid var(--fg-max)",
  transition: "background 140ms ease, box-shadow 140ms ease",
};

export function CompositionMathTrigger({
  open,
  onToggle,
  label = "HOW DOES IT WORK?",
}: {
  open: boolean;
  onToggle: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={open}
      onClick={onToggle}
      style={{
        ...triggerBtnBase,
        background: open ? "rgba(255,255,255,0.14)" : "transparent",
        boxShadow: open ? "0 0 10px rgba(255,255,255,0.18)" : "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.14)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = open
          ? "rgba(255,255,255,0.14)"
          : "transparent";
      }}
    >
      ?
    </button>
  );
}

// --------------------------------------------------------------------------
// Shared content pieces
// --------------------------------------------------------------------------

const h2: CSSProperties = {
  fontFamily: "var(--f-sans)",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--fg-max)",
  margin: 0,
  letterSpacing: "-0.005em",
  textTransform: "none",
};

const body: CSSProperties = {
  fontFamily: "var(--f-sans)",
  fontSize: 11.5,
  lineHeight: 1.5,
  color: "var(--fg-body)",
  textTransform: "none",
  margin: 0,
};

const mono9: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 9,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--fg-meta)",
  fontWeight: 600,
};

function TermChip({
  color,
  children,
  large,
}: {
  color: string;
  children: ReactNode;
  large?: boolean;
}) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: large ? 10 : 9,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        fontWeight: 600,
        color,
        padding: large ? "5px 10px" : "3px 7px",
        borderRadius: 2,
        border: `1px solid ${color}66`,
        background: `linear-gradient(135deg, ${color}22 0%, ${color}0a 100%)`,
        boxShadow: `inset 0 0 12px ${color}14, 0 0 8px ${color}18`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Op({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        fontSize: 12,
        color: "var(--fg-meta)",
        fontWeight: 400,
      }}
    >
      {children}
    </span>
  );
}

function UseCasePill({
  label,
  body,
  color,
}: {
  label: string;
  body: string;
  color: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "64px 1fr",
        alignItems: "center",
        gap: 8,
        padding: "7px 10px 7px 12px",
        borderRadius: 2,
        border: `1px solid ${color}55`,
        background: `linear-gradient(110deg, ${color}22 0%, ${color}08 45%, transparent 100%)`,
        boxShadow: `inset 2px 0 0 ${color}, 0 0 14px ${color}10`,
      }}
    >
      <span
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 9,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          fontWeight: 700,
          color,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 11,
          color: "var(--fg-body)",
          lineHeight: 1.35,
        }}
      >
        {body}
      </span>
    </div>
  );
}

const USE_CASES: ReadonlyArray<{
  label: string;
  body: string;
  color: string;
}> = [
  {
    label: "HEDGE",
    body: "Cap tail risk on a directional perp.",
    color: HEDGE_COLOR,
  },
  {
    label: "AMPLIFY",
    body: "Stack with a same-direction prediction for a catalyst.",
    color: AMPLIFY_COLOR,
  },
  {
    label: "HARVEST",
    body: "Monetize the high-probability side of a market.",
    color: HARVEST_COLOR,
  },
];

// --------------------------------------------------------------------------
// Panel — tall-thin layout to render in the perps aside, below the accordion.
// --------------------------------------------------------------------------

export function CompositionMathPanel({
  perp,
  pred,
  perpSymbol,
  mark,
  payoff,
  onClose,
}: {
  perp: PerpLeg;
  pred: PredictionLeg;
  perpSymbol: string;
  mark: number;
  payoff: ReadonlyArray<PayoffPoint>;
  onClose: () => void;
}) {
  const margin = perpMargin(perp.size, perp.entry, perp.leverage);
  const totalAtMark = perpPnl(perp, mark) + predPnl(pred, mark);
  const gMax = maxGain(payoff);
  const lMax = maxLoss(payoff);
  const directionOp = pred.direction === "above" ? "≥" : "≤";
  const perDollarMove = perp.size;
  const perDollarSign = perp.side === "long" ? "+" : "−";
  const entryFrac = pred.entryCents / 100;
  const winAmt = pred.shares * (1 - entryFrac);
  const loseAmt = pred.shares * entryFrac;
  const winsWhen = pred.outcome === "yes" ? "YES" : "NO";
  const losesWhen = pred.outcome === "yes" ? "NO" : "YES";

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        boxShadow: `0 -1px 0 rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)`,
        background: `linear-gradient(180deg, rgba(255,255,255,0.02) 0%, var(--bg-1) 100%)`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid var(--line-2)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            ...mono9,
            color: "var(--fg-max)",
            letterSpacing: "0.2em",
            fontWeight: 700,
          }}
        >
          HOW IT WORKS
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            width: 20,
            height: 20,
            borderRadius: 2,
            background: "transparent",
            border: "1px solid var(--line-3)",
            color: "var(--fg-meta)",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            padding: 0,
            fontSize: 11,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Body (scrolls) */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          padding: "12px 14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Section 1 — What are composites? */}
        <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h3 style={h2}>What are &quot;composites&quot;?</h3>
          <p style={body}>
            A single derivative you assemble yourself from two independent legs.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              justifyContent: "center",
              flexWrap: "wrap",
              padding: "10px 6px",
              borderRadius: 2,
              border: "1px dashed var(--line-2)",
              background:
                "linear-gradient(180deg, rgba(158,203,255,0.04), transparent 80%)",
            }}
          >
            <TermChip color={PERP_COLOR}>1× PERP</TermChip>
            <Op>×</Op>
            <TermChip color={PRED_COLOR}>1× PRED</TermChip>
            <Op>=</Op>
            <TermChip color={BRAND} large>
              COMPOSITE
            </TermChip>
          </div>
          <p style={body}>
            Each leg settles on its own venue, but their combined P&amp;L forms
            one curve with a step at the prediction&apos;s resolution.
          </p>
        </section>

        <Divider />

        {/* Section 2 — What's the point? */}
        <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h3 style={h2}>What&apos;s the point?</h3>
          <p style={body}>
            Shape a payoff you couldn&apos;t buy off the shelf — bend the curve
            around a specific event or price.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {USE_CASES.map((u) => (
              <UseCasePill
                key={u.label}
                label={u.label}
                body={u.body}
                color={u.color}
              />
            ))}
          </div>
          <div
            style={{
              ...mono9,
              fontSize: 9,
              color: "var(--fg-dim)",
              textAlign: "center",
            }}
          >
            · and more ·
          </div>
        </section>

        <Divider />

        {/* Section 3 — How it works (numbers) */}
        <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h3 style={h2}>How it works</h3>

          {/* Perp leg */}
          <div style={miniCard(PERP_COLOR)}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <TermChip color={PERP_COLOR}>PERP</TermChip>
              <Mono>
                {perp.side.toUpperCase()} {perp.size} {perpSymbol}
              </Mono>
            </div>
            <BulletRow
              label={`Each $1 in ${perpSymbol}`}
              value={`${perDollarSign}${perDollarMove} × $1`}
            />
            <BulletRow
              label="Liquidates at"
              value={formatPrice(perp.liquidation)}
              valueColor="var(--dn)"
            />
            <BulletRow
              label="Max loss on liq"
              value={signedUsd(-margin)}
              valueColor="var(--dn)"
            />
          </div>

          {/* Prediction leg */}
          <div style={miniCard(PRED_COLOR)}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <TermChip color={PRED_COLOR}>PRED</TermChip>
              <Mono>
                {pred.outcome.toUpperCase()} {pred.shares} @{" "}
                {pred.entryCents.toFixed(1)}¢
              </Mono>
            </div>
            <BulletRow
              label={`If ${perpSymbol} ${directionOp} ${formatPrice(pred.threshold)}`}
              value={signedUsd(
                pred.outcome === "yes" ? winAmt : -loseAmt,
              )}
              valueColor={
                pred.outcome === "yes" ? "var(--up)" : "var(--dn)"
              }
              hint={`${winsWhen} wins`}
            />
            <BulletRow
              label="Otherwise"
              value={signedUsd(
                pred.outcome === "yes" ? -loseAmt : winAmt,
              )}
              valueColor={
                pred.outcome === "yes" ? "var(--dn)" : "var(--up)"
              }
              hint={`${losesWhen} wins`}
            />
          </div>

          {/* Composite */}
          <div style={miniCard(BRAND)}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <TermChip color={BRAND}>COMPOSITE</TermChip>
              <span
                style={{
                  fontFamily: "var(--f-mono)",
                  fontSize: 9,
                  color: "var(--fg-dim)",
                  letterSpacing: "0.14em",
                }}
              >
                KINK @ {formatPrice(pred.threshold)}
              </span>
            </div>
            <BulletRow
              label="At mark"
              value={signedUsd(totalAtMark)}
              valueColor={totalAtMark >= 0 ? "var(--up)" : "var(--dn)"}
            />
            <BulletRow
              label="Max gain"
              value={signedUsd(gMax)}
              valueColor="var(--up)"
            />
            <BulletRow
              label="Max loss"
              value={signedUsd(lMax)}
              valueColor="var(--dn)"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

function Divider() {
  return (
    <div
      style={{
        height: 1,
        background: "var(--line-1)",
        margin: "2px 0",
      }}
    />
  );
}

function miniCard(color: string): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "9px 11px",
    borderRadius: 2,
    border: `1px solid ${color}33`,
    background: `linear-gradient(180deg, ${color}0a, transparent 70%)`,
    boxShadow: `inset 2px 0 0 ${color}88`,
  };
}

function BulletRow({
  label,
  value,
  valueColor,
  hint,
}: {
  label: string;
  value: string;
  valueColor?: string;
  hint?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 10,
        paddingTop: 4,
        borderTop: "1px solid var(--line-1)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--f-sans)",
          fontSize: 11,
          color: "var(--fg-body)",
          lineHeight: 1.35,
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          whiteSpace: "nowrap",
        }}
      >
        {hint && (
          <span
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 8,
              color: "var(--fg-dim)",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            {hint}
          </span>
        )}
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: valueColor ?? "var(--fg-max)",
          }}
        >
          {value}
        </span>
      </span>
    </div>
  );
}

function Mono({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--f-mono)",
        color: "var(--fg-max)",
        fontVariantNumeric: "tabular-nums",
        fontSize: 10.5,
      }}
    >
      {children}
    </span>
  );
}
